/**
 * Maps a Conflui conversation onto assistant-ui's message model (external-store runtime). The conversation transport is
 * unchanged — `useConversationStream` still owns the REST backfill, the live socket and sending; these pure functions
 * only translate what it holds into `ThreadMessageLike`s:
 *
 * - `text` parts → assistant-ui text parts (rendered as safe markdown);
 * - every other Conflui part (card, proposal, interaction_request, table, …) → a `data-<type>` part whose `data` is
 *   the original part, rendered by the existing per-part renderers;
 * - tool rows → `tool-call` parts (a row the turn wrote per call carries its input and result, `unified-tool-row.ts`);
 * - the live turn (thinking, tool calls, streamed text) → one running assistant message at the end.
 */
import type { AppendMessage, CompleteAttachment, ThreadMessageLike } from '@assistant-ui/react';
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
import { parseUnifiedToolCallRow, type UnifiedToolCallRow } from '@confluent/shared/lib/orchestrator/unified-tool-row';
import type { ConversationEventsState } from '../../hooks/useConversationEvents.js';
import type { StreamMessage } from '../../hooks/useConversationStream.js';
import type { MessageAttachment } from '../../generated/api/types.gen.js';
import { messageAttachmentId } from './message-attachment-ids.js';

type MessageContent = Exclude<ThreadMessageLike['content'], string>;
type ContentPart = MessageContent[number];

/** The id of the synthetic message that carries the live turn. */
export const LIVE_TURN_ID = 'live-turn';

/** The assistant-ui part type a Conflui part renders as. */
export type ConversationDataPartType = `data-${Exclude<ConversationMessagePart['type'], 'text'>}`;

function toContentPart(part: ConversationMessagePart): ContentPart {
  if (part.type === 'text') return { type: 'text', text: part.content };
  return { type: `data-${part.type}`, data: part };
}

/**
 * The stored conversation as assistant-ui messages. A turn writes one tool row per tool call (twenty on a multi-step
 * request), so consecutive tool rows fold into a single assistant message carrying all their tool calls.
 */
export function toThreadMessages(messages: readonly StreamMessage[]): ThreadMessageLike[] {
  const out: ThreadMessageLike[] = [];
  let previousWasCallRow = false;
  for (const message of messages) {
    const converted = toThreadMessage(message);
    const last = out[out.length - 1];
    // Only rows of tool calls fold together; a tool row without calls keeps its own id (a surface may draw it).
    const isCallRow =
      message.role === 'tool' &&
      ((message.toolCalls?.length ?? 0) > 0 || parseUnifiedToolCallRow(message.content) !== null);
    if (isCallRow && previousWasCallRow && last && typeof last.content !== 'string') {
      out[out.length - 1] = { ...last, content: [...last.content, ...(converted.content as MessageContent)] };
    } else {
      out.push(converted);
    }
    previousWasCallRow = isCallRow;
  }
  return out;
}

/** A stored message's tool calls as assistant-ui tool-call parts. */
function toolCallParts(message: StreamMessage): ContentPart[] {
  return (message.toolCalls ?? []).map(
    (call, index): ContentPart => ({
      type: 'tool-call',
      toolCallId: `${message.id}:${index}`,
      toolName: call.name,
      argsText: JSON.stringify(call.args ?? {}),
      result: call.result,
    }),
  );
}

/** A turn's per-call tool row as a tool-call part: its input, and its result or the reason it failed. */
function unifiedToolCallPart(row: UnifiedToolCallRow): ContentPart {
  const failed = row.status !== 'ok';
  return {
    type: 'tool-call',
    toolCallId: row.toolUseId,
    toolName: row.toolName,
    argsText: row.input ?? '{}',
    // Rows written before 2026-10-17 kept no result: say how the call ended.
    result: failed ? (row.error ?? 'Failed') : (row.result ?? 'Done'),
    isError: failed,
  };
}

/** Where a system line keeps the parts assistant-ui cannot hold (`ThreadMessageLike.metadata.custom`). */
const SYSTEM_LINE_PARTS = 'systemLineParts';

/** The parts a system line keeps beside its one text part, or null when it has none (`toThreadMessage`). */
export function systemLineParts(custom: Record<string, unknown> | undefined): ConversationMessagePart[] | null {
  const parts = custom?.[SYSTEM_LINE_PARTS];
  // Written only by `toThreadMessage` below, from the stored message's own parts.
  return Array.isArray(parts) ? (parts as ConversationMessagePart[]) : null;
}

/** One stored conversation message as an assistant-ui message. */
export function toThreadMessage(message: StreamMessage): ThreadMessageLike {
  const createdAt = new Date(message.createdAt);

  if (message.role === 'tool') {
    const calls = toolCallParts(message);
    const row = calls.length === 0 ? parseUnifiedToolCallRow(message.content) : null;
    return {
      id: message.id,
      role: 'assistant',
      createdAt,
      status: { type: 'complete', reason: 'stop' },
      content: calls.length > 0 ? calls : row ? [unifiedToolCallPart(row)] : [{ type: 'text', text: message.content }],
    };
  }

  const body: MessageContent =
    message.parts && message.parts.length > 0
      ? message.parts.map(toContentPart)
      : [{ type: 'text', text: message.content }];
  // assistant-ui accepts a system message only as one text part (it throws otherwise). A system line with more — a
  // notice's card, an employee's run card (author, text, card) — keeps its parts beside, and the thread draws them.
  if (message.role === 'system' && (body.length !== 1 || body[0]?.type !== 'text')) {
    return {
      id: message.id,
      role: 'system',
      createdAt,
      content: [{ type: 'text', text: message.content }],
      metadata: { custom: { [SYSTEM_LINE_PARTS]: message.parts } },
    };
  }
  // An assistant row can carry the tool calls it made alongside its text.
  const content: MessageContent = message.role === 'assistant' ? [...body, ...toolCallParts(message)] : body;

  return {
    id: message.id,
    role: message.role,
    createdAt,
    content,
    ...(message.role === 'assistant' ? { status: { type: 'complete', reason: 'stop' } as const } : {}),
    // A person's attached files, drawn by the surface's attachment components (`ThreadRenderers.messageAttachments`).
    ...(message.role === 'user' && message.attachments?.length
      ? { attachments: message.attachments.map(toThreadAttachment) }
      : {}),
  };
}

/** A file a message carries as an assistant-ui attachment, named by its upload (`message-attachment-ids.ts`). */
export function toThreadAttachment(file: MessageAttachment): CompleteAttachment {
  return {
    id: messageAttachmentId(file.uploadId),
    type: file.kind,
    name: file.name,
    contentType: file.mediaType,
    status: { type: 'complete' },
    content: [],
  };
}

type LiveEvents = Pick<ConversationEventsState, 'streamingMessage' | 'agentThinking' | 'orchestratorTurn'>;

/** Whether a turn is in flight (the composer shows Stop / the thread shows the running message). */
export function isTurnRunning(events: LiveEvents, awaitingAssistant: boolean): boolean {
  const status = events.orchestratorTurn.status;
  return (
    awaitingAssistant ||
    events.agentThinking ||
    events.streamingMessage !== null ||
    status === 'thinking' ||
    status === 'tool_calling' ||
    status === 'streaming_text'
  );
}

/**
 * The live turn as one running assistant message: a reasoning part while the agent thinks, a tool-call part per active
 * tool, then the text streamed so far. `null` when nothing is in flight.
 */
export function liveTurnMessage(events: LiveEvents, awaitingAssistant: boolean): ThreadMessageLike | null {
  if (!isTurnRunning(events, awaitingAssistant)) return null;
  const turn = events.orchestratorTurn;
  const content: ContentPart[] = [];

  if (turn.status === 'thinking' || (events.agentThinking && turn.activeToolCalls.size === 0)) {
    content.push({ type: 'reasoning', text: 'Thinking…' });
  }
  for (const [toolUseId, call] of turn.activeToolCalls) {
    content.push({
      type: 'tool-call',
      toolCallId: toolUseId,
      toolName: call.toolName,
      argsText: call.inputPreview,
      ...(call.status === 'pending'
        ? {}
        : {
            result: call.status === 'failed' ? (call.error?.message ?? 'Failed') : 'Done',
            isError: call.status === 'failed',
          }),
    });
  }
  const text = events.streamingMessage?.content || turn.streamingText;
  if (text) content.push({ type: 'text', text });

  return {
    id: LIVE_TURN_ID,
    role: 'assistant',
    content: content.length > 0 ? content : [{ type: 'reasoning', text: 'Thinking…' }],
    status: { type: 'running' },
  };
}

/** The plain text a person typed into the composer (assistant-ui hands over parts). */
export function appendMessageText(message: AppendMessage): string {
  return message.content
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('')
    .trim();
}
